import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  Slide,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation } from "react-router-dom";
import namamiLogo from "../assets/namami-logo.png";

export default function Navbar() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  const menuItems = ["Home", "About", "Services", "Transition", "Reports", "Contact"];

  // === Detect Scroll ===
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isOnHero = location.pathname === "/" && !scrolled;
  const textColor = isOnHero ? "#fff" : theme.palette.text.primary;

  return (
    <Slide appear={false} direction="down" in={true}>
      <AppBar
        position="fixed"
        elevation={scrolled ? 3 : 0}
        sx={{
          transition: "all 0.4s ease",
          backgroundColor: scrolled
            ? "rgba(255, 255, 255, 0.78)"
            : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.08)" : "none",
          color: textColor,
          py: 0.5,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, sm: 4, md: 8 } }}>
          {/* === Logo Section === */}
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              component="img"
              src={namamiLogo}
              alt="Namami Logo"
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                     transition: "filter 0.3s ease",
              }}
            />
            <Typography
              variant="h6"
              fontWeight={700}
              component={Link}
              to="/"
              sx={{
                textDecoration: "none",
                color: textColor,
                letterSpacing: "0.5px",
                transition: "color 0.3s ease",
                "&:hover": { opacity: 0.8 },
                   filter: isOnHero ? "brightness(0) invert(1)" : "none",
        
              }}
            >
              Namami India
            </Typography>
          </Box>

          {/* === Desktop Menu === */}
          {!isMobile && (
            <Box>
              {menuItems.map((label) => (
                <Button
                  key={label}
                  component={Link}
                  to={`/${label === "Home" ? "" : label.toLowerCase()}`}
                  sx={{
                    mx: 1.2,
                    fontWeight: 500,
                    color: textColor,
                    textTransform: "none",
                    letterSpacing: "0.3px",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -3,
                      left: 0,
                      width: 0,
                      height: 2,
                      backgroundColor: theme.palette.primary.main,
                      transition: "width 0.3s ease",
                    },
                    "&:hover::after": { width: "100%" },
                    "&:hover": {
                      color: scrolled
                        ? theme.palette.primary.main
                        : "rgba(255,255,255,0.9)",
                    },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
          )}

          {/* === Mobile Menu Button === */}
          {isMobile && (
            <IconButton
              edge="end"
              onClick={() => setOpenDrawer(true)}
              sx={{
                color: textColor,
                transition: "color 0.3s ease",
              }}
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          )}
        </Toolbar>

        {/* === Mobile Drawer === */}
        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          PaperProps={{
            sx: {
              width: "100%",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.95) 10%, rgba(240,240,240,0.9) 90%)",
              backdropFilter: "blur(20px)",
              color: "#111",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              p: 3,
            },
          }}
        >
          {/* Drawer Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            mb={2}
          >
            <Typography variant="h6" fontWeight={700}>
              Menu
            </Typography>
            <IconButton onClick={() => setOpenDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ width: "60%", mb: 3 }} />

          {/* Drawer Items */}
          <List sx={{ width: "100%" }}>
            {menuItems.map((label, index) => (
              <ListItem key={label} disablePadding sx={{ justifyContent: "center" }}>
                <ListItemButton
                  component={Link}
                  to={`/${label === "Home" ? "" : label.toLowerCase()}`}
                  onClick={() => setOpenDrawer(false)}
                  sx={{
                    py: 2,
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      textAlign: "center",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Box mt="auto" pt={2} borderTop="1px solid rgba(0,0,0,0.1)">
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} Namami India Support Services
            </Typography>
          </Box>
        </Drawer>
      </AppBar>
    </Slide>
  );
}
